/**
 * The two marks both rails draw — one component each, so the wide and the narrow rail cannot
 * draw a chapter, or a planner, two different ways.
 */

import { RAIL_ICONS, RAIL_ICON_FALLBACK } from './rail-icons';
import type { ChapterNav } from '../../features/registry';

/** A chapter's icon, from its manifest's name — `rail-icons.ts` says why a name. */
export function ChapterIconGlyph(
  { chapter, size = 18 }: { readonly chapter: ChapterNav; readonly size?: number },
): React.JSX.Element {
  const Icon = chapter.icon === undefined ? RAIL_ICON_FALLBACK : RAIL_ICONS[chapter.icon];
  return <Icon size={size} aria-hidden />;
}

/**
 * Up to two initials of a name, for the identity mark — audit v5b R-08.
 *
 * Letters, not code units: `Array.from` walks code points, so an initial outside the BMP is
 * one letter rather than half a surrogate pair. Words are split on whitespace only; a login
 * such as `e2e-fixture` is one word and gives one initial, which is what it is.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/u).filter((w) => w !== '');
  const first = (w: string | undefined): string => (w === undefined ? '' : Array.from(w)[0] ?? '');
  const letters = words.length > 1 ? first(words[0]) + first(words[words.length - 1]) : first(words[0]);
  return letters.toLocaleUpperCase();
}

/**
 * A square with the planner's initials — the mockup's avatar. `aria-hidden`: the name it
 * abbreviates is always written beside it or carried by the control it sits in.
 */
export function IdentityMark({ name }: { readonly name: string }): React.JSX.Element {
  return (
    <span
      aria-hidden
      translate="no"
      className="bg-rail-raised border-rail-hairline text-micro text-rail-fg flex size-6 shrink-0 items-center justify-center rounded-control border font-semibold"
    >
      {initialsOf(name)}
    </span>
  );
}
